<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import type { EmailTagHint } from '../types/clubInvitation.types'
import { EMAIL_SEPARATOR_REGEX, isValidEmail, splitEmails } from '../utils/emailList'

/**
 * Chip input for e-mail addresses.
 * Separators: comma, semicolon, space, Enter, Tab, new line. Pasted lists are split into chips.
 * Backspace in an empty input turns the previous chip back into editable text,
 * a click on a chip edits it in place, Escape cancels the edit.
 */
const props = withDefaults(defineProps<{
  modelValue: string[]
  max?: number
  // Markers per address, e.g. "sent 12.10"
  hints?: Record<string, EmailTagHint[]>
  // Addresses rejected by the server - shown as invalid
  invalidEmails?: string[]
  placeholder?: string
  disabled?: boolean
  inputId?: string
  describedBy?: string
}>(), {
  max: 200,
  hints: () => ({}),
  invalidEmails: () => [],
  placeholder: '',
  disabled: false
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const { t } = useI18n()

// Local mirror, so several operations in one event always see the latest list
const chips = ref<string[]>([...props.modelValue])
const text = ref('')
// Position of the input while a chip is edited in place (null = at the end)
const editIndex = ref<number | null>(null)
// Value of the edited chip before the edit - restored on Escape
const editOriginal = ref<string | null>(null)
const focused = ref(false)
const flashEmail = ref<string | null>(null)
const limitMessage = ref<string | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)

let flashTimer: ReturnType<typeof setTimeout> | null = null
let limitTimer: ReturnType<typeof setTimeout> | null = null

watch(() => props.modelValue, (value) => {
  const same = value.length === chips.value.length && value.every((email, i) => email === chips.value[i])
  if (!same) chips.value = [...value]
})

const setChips = (list: string[]) => {
  chips.value = list
  emit('update:modelValue', [...list])
}

const serverInvalid = computed(() => new Set(props.invalidEmails))
const isInvalid = (email: string) => !isValidEmail(email) || serverInvalid.value.has(email)

const insertIndex = computed(() => editIndex.value ?? chips.value.length)

const inputStyle = computed(() => {
  if (editIndex.value === null) return { order: chips.value.length * 2 }
  // Editing in place: sit right before the chip that follows the edited one, sized to the text
  return { order: editIndex.value * 2 - 1, width: `${Math.max(text.value.length + 2, 10)}ch` }
})

const focus = (caretAtEnd = false) => {
  nextTick(() => {
    const el = inputEl.value
    if (!el) return
    el.focus()
    if (caretAtEnd) el.setSelectionRange(el.value.length, el.value.length)
  })
}

const flash = (email: string) => {
  if (flashTimer) clearTimeout(flashTimer)
  // Reset first, so the animation restarts for repeated duplicates
  flashEmail.value = null
  nextTick(() => {
    flashEmail.value = email
    flashTimer = setTimeout(() => { flashEmail.value = null }, 700)
  })
}

const showLimit = (skipped: number) => {
  if (limitTimer) clearTimeout(limitTimer)
  limitMessage.value = t('clubInvitations.limitReached', { max: props.max, count: skipped })
  limitTimer = setTimeout(() => { limitMessage.value = null }, 6000)
}

// Insert addresses at the input position; returns how many were added
const addEmails = (emails: string[]): number => {
  const list = [...chips.value]
  let index = insertIndex.value
  let added = 0
  let skipped = 0

  for (const email of emails) {
    if (list.includes(email)) {
      flash(email)
      continue
    }
    if (list.length >= props.max) {
      skipped++
      continue
    }
    list.splice(index, 0, email)
    index++
    added++
  }

  if (skipped > 0) showLimit(skipped)
  if (added > 0) setChips(list)
  return added
}

const finishEdit = () => {
  editIndex.value = null
  editOriginal.value = null
}

// Turn the typed text into chips
const commit = () => {
  const emails = splitEmails(text.value)
  text.value = ''
  if (emails.length > 0) addEmails(emails)
  finishEdit()
}

const remove = (email: string) => {
  if (props.disabled) return
  setChips(chips.value.filter(item => item !== email))
  focus()
}

const startEdit = (email: string) => {
  if (props.disabled) return
  if (text.value.trim() !== '') commit()

  const index = chips.value.indexOf(email)
  if (index === -1) return

  const list = [...chips.value]
  list.splice(index, 1)
  setChips(list)
  text.value = email
  editOriginal.value = email
  // Editing the last chip simply puts the input back at the end
  editIndex.value = index >= list.length ? null : index
  focus(true)
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.isComposing) return
  const key = event.key
  const hasText = text.value.trim() !== ''

  if (key === 'Enter' || key === ',' || key === ';' || key === ' ' || (key === 'Tab' && hasText)) {
    event.preventDefault()
    if (hasText) commit()
    else finishEdit()
    return
  }

  if (key === 'Backspace' && text.value === '') {
    const index = insertIndex.value
    if (index === 0) {
      finishEdit()
      return
    }
    event.preventDefault()
    const list = [...chips.value]
    const [email] = list.splice(index - 1, 1)
    setChips(list)
    text.value = email ?? ''
    editOriginal.value = email ?? null
    editIndex.value = index - 1 >= list.length ? null : index - 1
    focus(true)
    return
  }

  if (key === 'Escape' && editOriginal.value !== null) {
    event.preventDefault()
    event.stopPropagation()
    text.value = editOriginal.value
    commit()
    focus()
  }
}

// Separators that arrive without a usable keydown (mobile keyboards, IME)
const onInput = () => {
  if (!EMAIL_SEPARATOR_REGEX.test(text.value)) return

  const match = text.value.match(/^([\s\S]*[\s,;])([^\s,;]*)$/)
  const done = match?.[1] ?? text.value
  const rest = match?.[2] ?? ''

  text.value = rest
  const added = addEmails(splitEmails(done))
  if (editIndex.value !== null) {
    if (rest === '') finishEdit()
    else editIndex.value += added
  }
}

const onPaste = (event: ClipboardEvent) => {
  const pasted = event.clipboardData?.getData('text') ?? ''
  if (!pasted) return
  event.preventDefault()

  const input = event.target as HTMLInputElement
  const start = input.selectionStart ?? text.value.length
  const end = input.selectionEnd ?? text.value.length
  text.value = text.value.slice(0, start) + pasted + text.value.slice(end)
  commit()
  focus()
}

const onFocus = () => {
  focused.value = true
}

const onBlur = () => {
  focused.value = false
  if (text.value.trim() !== '' || editIndex.value !== null) commit()
}

const chipClass = (email: string) => [
  'inline-flex items-center gap-1 max-w-full pl-2.5 pr-1 py-0.5 rounded-full border text-sm leading-6 transition-colors',
  isInvalid(email)
    ? 'bg-red-50 border-red-400 text-red-800'
    : 'bg-blue-50 border-blue-200 text-blue-900',
  flashEmail.value === email ? 'chip-flash' : ''
]

const hintClass = (hint: EmailTagHint) => [
  'shrink-0 px-1.5 rounded-full text-[10px] font-medium leading-4 whitespace-nowrap',
  hint.tone === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-white text-gray-600 border border-gray-200'
]

onBeforeUnmount(() => {
  if (flashTimer) clearTimeout(flashTimer)
  if (limitTimer) clearTimeout(limitTimer)
})

defineExpose({ focus, commit })
</script>

<template>
  <div>
    <div
      role="group"
      :aria-label="t('clubInvitations.inputAriaLabel')"
      :class="[
        'w-full min-h-[7rem] max-h-72 overflow-y-auto px-2 py-2 border-2 rounded flex flex-wrap items-start content-start gap-1.5 transition-colors',
        disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-text',
        focused ? 'border-blue-600 ring-2 ring-blue-100' : 'border-gray-300'
      ]"
      @click.self="focus()"
    >
      <span
        v-for="(email, index) in chips"
        :key="email"
        :class="chipClass(email)"
        :style="{ order: index * 2 }"
        :title="isInvalid(email) ? t('clubInvitations.invalidEmail') : undefined"
      >
        <svg
          v-if="isInvalid(email)"
          class="w-3.5 h-3.5 shrink-0 text-red-600"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <button
          type="button"
          class="truncate text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          :disabled="disabled"
          :aria-label="isInvalid(email) ? `${t('clubInvitations.editEmail', { email })} — ${t('clubInvitations.invalidEmail')}` : t('clubInvitations.editEmail', { email })"
          @click="startEdit(email)"
        >{{ email }}</button>
        <span v-for="hint in hints[email] || []" :key="hint.label" :class="hintClass(hint)">{{ hint.label }}</span>
        <button
          type="button"
          class="shrink-0 w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          :disabled="disabled"
          :aria-label="t('clubInvitations.removeEmail', { email })"
          @click="remove(email)"
        >
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </span>

      <input
        :id="inputId"
        ref="inputEl"
        v-model="text"
        type="text"
        inputmode="email"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        :disabled="disabled"
        :placeholder="chips.length === 0 && editIndex === null ? placeholder : ''"
        :aria-label="t('clubInvitations.inputAriaLabel')"
        :aria-describedby="describedBy"
        :style="inputStyle"
        :class="[
          'min-w-0 py-0.5 px-1 text-sm leading-6 bg-transparent border-0 outline-none focus:ring-0',
          editIndex === null ? 'flex-1 min-w-[10rem]' : 'rounded bg-blue-50'
        ]"
        @keydown="onKeydown"
        @input="onInput"
        @paste="onPaste"
        @focus="onFocus"
        @blur="onBlur"
      >
    </div>
    <p v-if="limitMessage" class="mt-1 text-xs text-amber-700" role="status">{{ limitMessage }}</p>
  </div>
</template>

<style scoped>
.chip-flash {
  animation: chip-flash 0.7s ease-in-out;
}

@keyframes chip-flash {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
  }
  30% {
    transform: scale(1.06);
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.6);
  }
}
</style>
